import type { ReactNode } from 'react'
import { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { ProductVariantSelector } from '@haus-storefront-react/product-variant-selector'
import { DefaultProductVariantUrlStrategy } from '@haus-storefront-react/strategies'
import { useEventBusOn, productChannel } from '@haus-storefront-react/core'
import { AriaLiveRegion, WidgetReadyBoundary } from '@components'
import { getVendureProductId } from '../../utilities/utilities'
import './VariantSelectorSkeleton.css'
import styles from './VariantSelector.module.scss'

interface VariantSelectorWidgetProps {
  numOptionGroups?: number
}

function VariantContentWrapper({
  children,
  optionGroups,
  onContentReady,
}: {
  children: ReactNode
  optionGroups: ReadonlyArray<{ code: string }> | undefined
  onContentReady: () => void
}) {
  const hasResolvedOptionGroups = optionGroups != null

  useEffect(() => {
    if (!hasResolvedOptionGroups) return
    requestAnimationFrame(() => {
      requestAnimationFrame(() => onContentReady())
    })
  }, [hasResolvedOptionGroups, onContentReady])

  return <>{children}</>
}

const VariantSelectorWidget = ({
  numOptionGroups: _numOptionGroups = 1,
}: VariantSelectorWidgetProps) => {
  const { t } = useTranslation()
  const [variantAnnouncement, setVariantAnnouncement] = useState('')
  const [hasRootContent, setHasRootContent] = useState(false)
  const [selectedProductVariant] = useEventBusOn(productChannel, 'product:variant:selected')

  useEffect(() => {
    setHasRootContent(false)
  }, [selectedProductVariant?.id])

  const vendureProductId = getVendureProductId()
  const isReady = !vendureProductId || hasRootContent

  const handleSelectVariant = (
    option: { id: string; name: string },
    selectOption: (id: string) => void,
  ) => {
    selectOption(option.id)
    setVariantAnnouncement(
      t('a11y.productVariantOptions.aria.selectedVariant', {
        variantName: option.name,
      }),
    )
  }

  if (!vendureProductId) {
    return (
      <WidgetReadyBoundary isReady>
        <span />
      </WidgetReadyBoundary>
    )
  }

  if (!selectedProductVariant?.id) {
    return (
      <WidgetReadyBoundary isReady={false}>
        <div />
      </WidgetReadyBoundary>
    )
  }

  return (
    <WidgetReadyBoundary isReady={isReady}>
      <div className={styles.rootWithFallback}>
        <ProductVariantSelector.Root
          key={selectedProductVariant.id}
          productId={vendureProductId}
          initialVariantId={selectedProductVariant?.id}
          strategies={{ productVariantStoreStrategy: new DefaultProductVariantUrlStrategy() }}
        >
          {({ optionGroups }) => {
            return (
              <VariantContentWrapper
                optionGroups={optionGroups}
                onContentReady={() => setHasRootContent(true)}
              >
                <div className={styles.optionGroupsContainer}>
                  <AriaLiveRegion message={variantAnnouncement} />
                  {(optionGroups ?? []).map((optionGroup) => (
                    <ProductVariantSelector.OptionGroup
                      key={optionGroup.code}
                      optionGroup={optionGroup}
                      data-testid="option-group"
                      asChild
                    >
                      {({ options }) => (
                        <div className={styles.optionGroup}>
                          <div className={styles.optionGroupLabel}>{optionGroup.name}</div>

                          <div className={styles.optionsContainer}>
                            {options.map((option) => (
                              <ProductVariantSelector.Option
                                key={option.code}
                                optionId={option.id}
                                asChild
                              >
                                {({ option, selectOption, isSelected }) => (
                                  <button
                                    type="button"
                                    onClick={() => handleSelectVariant(option, selectOption)}
                                    aria-label={t('a11y.productVariantOptions.aria.selectOption', {
                                      optionName: option.name,
                                    })}
                                    aria-pressed={isSelected}
                                    className={`${styles.optionButton} ${
                                      isSelected ? styles.optionButtonSelected : ''
                                    }`}
                                  >
                                    {option.name}
                                  </button>
                                )}
                              </ProductVariantSelector.Option>
                            ))}
                          </div>
                        </div>
                      )}
                    </ProductVariantSelector.OptionGroup>
                  ))}
                </div>
              </VariantContentWrapper>
            )
          }}
        </ProductVariantSelector.Root>
      </div>
    </WidgetReadyBoundary>
  )
}

export default VariantSelectorWidget
